'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
  makeT,
  type Locale,
  type Translator,
  type UiOverrides,
} from './index';

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  overrides: UiOverrides;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  overrides: {},
});

/**
 * Mounted once in the root layout with the locale read from the cookie and the
 * admin-edited wording fetched server-side. The locale is held in client state
 * so toggling it re-renders every client component (which translate via
 * {@link useT}) instantly — no full reload.
 */
export function LocaleProvider({
  locale: initialLocale,
  overrides = {},
  children,
}: {
  locale: Locale;
  overrides?: UiOverrides;
  children: ReactNode;
}) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    // Persist so the server (marketing pages, <html lang>) renders the new
    // locale on the next request/navigation.
    document.cookie = `${LOCALE_COOKIE}=${next};path=/;max-age=31536000;samesite=lax`;
    // Keep the document language attribute in sync for a11y without reloading.
    document.documentElement.lang = next;
  }, []);

  const value = useMemo(() => ({ locale, setLocale, overrides }), [locale, setLocale, overrides]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): Locale {
  return useContext(LocaleContext).locale;
}

/**
 * Returns a setter that switches the locale instantly for client UI and
 * persists the choice in the cookie (so server-rendered pages follow on the
 * next navigation).
 */
export function useSwitchLocale(): (locale: Locale) => void {
  return useContext(LocaleContext).setLocale;
}

/** Translator for client components: `const t = useT();`. */
export function useT(): Translator {
  const { locale, overrides } = useContext(LocaleContext);
  return useMemo(() => makeT(locale, overrides), [locale, overrides]);
}
